import { type DefaultContext, gqlPromise, mutationPromise } from '@egw/clients'
import { type QueryClient } from '@tanstack/react-query'
import { createMutationHook } from '../../hooks'
import {
  type ActivateCardInput,
  type BankAccountActivityInput,
  type BankAccountActivityOutput,
  type BankAccountDetail,
  type CardStatusUpdateInput,
  type CardStatusUpdateOutput,
  type CompleteRelinkExternalAccountInput,
  type CompleteRelinkExternalAccountResult,
  type CompleteUpdatePlaidExternalAccountInput,
  type CompleteUpdatePlaidExternalAccountResult,
  type CreatePlaidLinkTokenV2Input,
  type CreatePlaidLinkTokenV2Output,
  type CreatePlaidMultiLinkTokenInput,
  type EditLinkedAchAccountNameInput,
  type EditLinkedAchAccountNameOutput,
  type InitiateRelinkExternalAccountInput,
  type InitiateRelinkExternalAccountOutput,
  type InitiateUpdatePlaidExternalAccountInput,
  type InitiateUpdatePlaidExternalAccountResult,
  type LinkAchAccountInput,
  type LinkAchAccountOutput,
  type LinkPlaidAchAccountInput,
  type LinkPlaidAchAccountOutput,
  type UnlinkAchAccountInput,
  type UnlinkAchAccountOutput,
  type UnlinkExternalAccountInput,
  type UnlinkExternalAccountResult,
  type UnlinkExternalAccountsByInstitutionInput,
  type UnlinkExternalAccountsByInstitutionResult,
  type UpdateAddressInput,
  type UpdateBankAccountNameInput,
  type UpdateBankAccountNameOutput,
  type UpdateEmailInput,
  type UpdateEmploymentInfoInput,
  type UpdateFinancialInfoInput,
  type UpdatePhoneInput,
  type UpdateProfileOutput,
  UpdateProfileStatus,
  type UpdateSuitabilityInput,
} from '../../models'
import { invalidateBankAccountNameUpdateQueries, poll } from '../../utils'

/**
 * Get bank account details based on alias
 * @param context - reference to the AbortController used to abort the call (optional)
 * @returns Promise<BankAccountDetail>
 * @example const bankAccountDetail = await getBankAccountDetail();
 */
export const getBankAccountDetail = async (alias: number, context?: DefaultContext): Promise<BankAccountDetail> => {
  const response = (await gqlPromise(
    {
      name: 'bankAccountDetail',
      query: `
        query bankAccountDetail($alias: Alias!) {
          bankAccountDetail(alias: $alias) {
            alias
            accountTitle
            accountType
            accountName
            routingNumber
            accountNumber
            apy
            status
            balance {
              total {
                currencyCode
                amount
              }
              available {
                currencyCode
                amount
              }
            }
            debitCard {
              cardId
              cardName
              maskedPAN
              cardStatus
              expDate
              needToSetPin
              formType
              cardStockId
              programId
              cardCreated
              cardLastStatusUpdated
              embossedLine1
              embossedLine2
              accountId
              profileId
              cardStatusReason
              merchantCredentialUpdate
              pinBlock
              pinSelection
              orderDetails {
                cardId
                cardStockId
                dateTime
                statusLastUpdatedDateTime
                embossedLine1
                orderReason
                primaryAccountNumber
                shippingMethod
                collateralId
                embossedLine2
                expirationDate
                formFactor
                orderId
                shippingAddress {
                  line1
                  line2
                  city
                  region
                  postalCode
                  country
                }
                status
              }
            }
            activity {
              activityId
              displayName
              date
              amount {
                currencyCode
                amount
              }
              isProcessing
              method
              label
              note
              ... on BankActivity {
                __typename
              }
              ... on OtherBankActivity {
                __typename
              }
              ... on AchBankActivity {
                __typename
                finInstOrMerchantName
                routingNumber
                accountName
              }
              ... on EftBankActivity {
                __typename
                acceptor
                eftType
                location {
                  city
                  country
                  postalCode
                  region
                  street
                }
              }
              ... on WireBankActivity {
                __typename
                direction
                institutionName
                imad
              }
            }
            achLimits {
              ...on AchLimits {
                __typename
                credit {
                  singleTransactionLimit
                  dailyLimit
                  monthlyLimit
                }
                debit {
                  singleTransactionLimit
                  dailyLimit
                  monthlyLimit
                }
              }
              ...on AchLimitsError {
                __typename
                errorType
              }
            }
          }
        }
      `,
    },
    {
      alias,
    },
    context,
  )) as { bankAccountDetail: BankAccountDetail }
  return response.bankAccountDetail
}

export const getBankActivity = async (
  bankAccountActivityInput: BankAccountActivityInput,
  context?: DefaultContext,
): Promise<BankAccountActivityOutput> => {
  const response = (await gqlPromise(
    {
      name: 'bankActivity',
      query: `
      query bankAccountActivity($bankAccountActivityInput: BankAccountActivityInput!) {
        bankAccountActivity(bankAccountActivityInput: $bankAccountActivityInput) {
          activity {
            activityId
            displayName
            date
            amount {
              amount
              currencyCode
            }
            isProcessing
            pendingBanner
            method
            label
            note
            ... on BankActivity {
              __typename
            }
            ... on OtherBankActivity {
              __typename
            }
            ... on AchBankActivity {
              __typename
              finInstOrMerchantName
              routingNumber
              accountName
            }
            ... on EftBankActivity {
              __typename
              acceptor
              eftType
              location {
                city
                country
                postalCode
                region
                street
              }
            }
            ... on WireBankActivity {
              __typename
              direction
              institutionName
              imad
            }
          }
        }
      }`,
    },
    { bankAccountActivityInput },
    context,
  )) as { bankAccountActivity: BankAccountActivityOutput }
  return response.bankAccountActivity
}

export const updateBankAccountName = async (
  updateBankAccountNameInput: UpdateBankAccountNameInput,
  context?: DefaultContext,
  queryClient?: QueryClient,
): Promise<UpdateBankAccountNameOutput> => {
  const response = (await mutationPromise(
    {
      name: 'updateBankAccountName',
      query: `
            mutation UpdateBankAccountName($updateBankAccountNameInput: UpdateBankAccountNameInput!) {
              updateBankAccountName(updateBankAccountNameInput: $updateBankAccountNameInput) {
                ... on UpdateBankAccountName {
                  __typename
                  isSuccessful
                }
                ... on UpdateBankAccountNameError {
                  __typename
                  errorType
                }
              }
            }
      `,
    },
    { updateBankAccountNameInput },
    context,
  )) as { updateBankAccountName: UpdateBankAccountNameOutput }

  const result = response.updateBankAccountName

  // Invalidate queries on successful bank account name update if queryClient is provided
  if (queryClient && result.__typename === 'UpdateBankAccountName' && result.isSuccessful) {
    invalidateBankAccountNameUpdateQueries({
      client: queryClient,
      bankAccountAlias: updateBankAccountNameInput.alias,
      refetchType: 'all',
    })
  }

  return result
}

export const useUpdateBankAccountName = createMutationHook<UpdateBankAccountNameInput, UpdateBankAccountNameOutput>(
  updateBankAccountName,
)

export const updateAddress = async (
  updateAddressInput: UpdateAddressInput,
  context?: DefaultContext,
): Promise<UpdateProfileOutput> => {
  const response = (await mutationPromise(
    {
      name: 'updateAddress',
      query: `mutation updateAddress($updateAddressInput: UpdateAddressInput!) {
          updateAddress(updateAddressInput: $updateAddressInput) {
            __typename
            ... on UpdateProfileSuccess {
              status
              __typename
            }
            ... on UpdateProfileError {
              type
              __typename
            }
          }
        }`,
    },
    { updateAddressInput },
    context,
  )) as { updateAddress: UpdateProfileOutput }
  return response.updateAddress
}

export const useUpdateCustomerAddress = createMutationHook<UpdateAddressInput, UpdateProfileOutput>(updateAddress)

export const updatePhone = async (
  updatePhoneInput: UpdatePhoneInput,
  context?: DefaultContext,
): Promise<UpdateProfileOutput> => {
  const response = (await mutationPromise(
    {
      name: 'updatePhone',
      query: `mutation updatePhone($updatePhoneInput: UpdatePhoneInput!) {
          updatePhone(updatePhoneInput: $updatePhoneInput) {
            __typename
            ... on UpdateProfileSuccess {
              __typename
              status
            }
            ... on UpdateProfileError {
              type
              __typename
            }
          }
        }
      `,
    },
    {
      updatePhoneInput,
    },
    context,
  )) as { updatePhone: UpdateProfileOutput }
  return response.updatePhone
}

export const pollUpdatePhoneStatus = async (context?: DefaultContext): Promise<UpdateProfileOutput> => {
  const checkPhoneStatusResponse = await poll<UpdateProfileOutput>(
    () => updatePhone({ getStatus: true }, context),
    (data: UpdateProfileOutput) =>
      (data.__typename === 'UpdateProfileSuccess' &&
        (data.status === UpdateProfileStatus.COMPLETE || data.status === UpdateProfileStatus.FAILED)) ||
      data.__typename === 'UpdateProfileError',
    500,
    60, // 30 seconds
  )
  return checkPhoneStatusResponse
}

export const updateEmail = async (
  updateEmailInput: UpdateEmailInput,
  context?: DefaultContext,
): Promise<UpdateProfileOutput> => {
  const response = (await mutationPromise(
    {
      name: 'updateEmail',
      query: `mutation updateEmail($updateEmailInput: UpdateEmailInput!) {
          updateEmail(updateEmailInput: $updateEmailInput) {
            __typename
            ... on UpdateProfileSuccess {
              __typename
              status
            }
            ... on UpdateProfileError {
              type
              __typename
            }
          }
        }
      `,
    },
    {
      updateEmailInput,
    },
    context,
  )) as { updateEmail: UpdateProfileOutput }
  return response.updateEmail
}

export const pollUpdateEmailStatus = async (context?: DefaultContext): Promise<UpdateProfileOutput> => {
  const checkEmailStatusResponse = await poll<UpdateProfileOutput>(
    () => updateEmail({ getStatus: true }, context),
    (data: UpdateProfileOutput) =>
      (data.__typename === 'UpdateProfileSuccess' &&
        (data.status === UpdateProfileStatus.COMPLETE || data.status === UpdateProfileStatus.FAILED)) ||
      data.__typename === 'UpdateProfileError',
    500,
    60, // 30 seconds
  )
  return checkEmailStatusResponse
}

/**
 * Update Employment Info
 * @param updateEmploymentInfoInput - input object containing employment info
 * @param context - reference to the AbortController used to abort the call (optional)
 * @returns Promise<UpdateProfileOutput>
 */
export const updateEmploymentInfo = async (
  updateEmploymentInfoInput: UpdateEmploymentInfoInput,
  context?: DefaultContext,
): Promise<UpdateProfileOutput> => {
  const response = (await mutationPromise(
    {
      name: 'UpdateEmploymentInfo',
      query: `
        mutation updateEmploymentInfo($updateEmploymentInfoInput: UpdateEmploymentInfoInput!) {
          updateEmploymentInfo(updateEmploymentInfoInput: $updateEmploymentInfoInput) {
            ... on UpdateProfileSuccess {
              __typename
              status
            }
            ... on UpdateProfileError {
              __typename
              type
            }
          }
        }
      `,
    },
    { updateEmploymentInfoInput },
    context,
  )) as { updateEmploymentInfo: UpdateProfileOutput }
  return response.updateEmploymentInfo
}

export const useUpdateEmploymentInfo = createMutationHook<UpdateEmploymentInfoInput, UpdateProfileOutput>(
  updateEmploymentInfo,
)

/**
 * Update Financial Info
 * @param updateFinancialInfoInput - input object containing financial info
 * @param context - reference to the AbortController used to abort the call (optional)
 * @returns Promise<UpdateProfileOutput>
 */
export const updateFinancialInfo = async (
  updateFinancialInfoInput: UpdateFinancialInfoInput,
  context?: DefaultContext,
): Promise<UpdateProfileOutput> => {
  const response = (await mutationPromise(
    {
      name: 'updateFinancialInfo',
      query: `
      mutation updateFinancialInfo($updateFinancialInfoInput: UpdateFinancialInfoInput!) {
        updateFinancialInfo(updateFinancialInfoInput: $updateFinancialInfoInput) {
          ... on UpdateProfileSuccess {
            status
            __typename
          }
          ... on UpdateProfileError {
            type
            __typename
          }
        }
      }
    `,
    },
    { updateFinancialInfoInput },
    context,
  )) as {
    updateFinancialInfo: UpdateProfileOutput
  }
  return response.updateFinancialInfo
}

export const useUpdateFinancialInfo = createMutationHook<UpdateFinancialInfoInput, UpdateProfileOutput>(
  updateFinancialInfo,
)

/**
 * Update Suitability Info
 * @param UpdateSuitabilityInput - input object containing suitability info
 * @param context - reference to the AbortController used to abort the call (optional)
 * @returns Promise<UpdateProfileOutput>
 */
export const updateSuitability = async (
  updateSuitabilityInput: UpdateSuitabilityInput,
  context?: DefaultContext,
): Promise<UpdateProfileOutput> => {
  const response = (await mutationPromise(
    {
      name: 'updateSuitability',
      query: `
      mutation updateSuitability($updateSuitabilityInput: UpdateSuitabilityInput!) {
        updateSuitability(updateSuitabilityInput: $updateSuitabilityInput) {
          ... on UpdateProfileSuccess {
            status
            __typename
          }
          ... on UpdateProfileError {
            type
            __typename
          }
        }
      }
    `,
    },
    { updateSuitabilityInput },
    context,
  )) as {
    updateSuitability: UpdateProfileOutput
  }
  return response.updateSuitability
}

export const useUpdateSuitability = createMutationHook<UpdateSuitabilityInput, UpdateProfileOutput>(updateSuitability)

/**
 * Block Card
 * @param CardStatusUpdateInput - input object containing cardId
 * @param context - reference to the AbortController used to abort the call (optional)
 * @returns Promise<CardStatusUpdateOutput>
 */
export const blockCard = async (
  cardStatusUpdateInput: CardStatusUpdateInput,
  context?: DefaultContext,
): Promise<CardStatusUpdateOutput> => {
  const response = (await mutationPromise(
    {
      name: 'blockCard',
      query: `
      mutation blockCard($cardStatusUpdateInput: CardStatusUpdateInput!) {
        blockCard(cardStatusUpdateInput: $cardStatusUpdateInput) {
          ... on CardStatusUpdateSuccess {
            __typename
            status
          }
          ... on CardStatusUpdateError {
            __typename
            errorType
          }
        }
      }
    `,
    },
    { cardStatusUpdateInput },
    context,
  )) as {
    blockCard: CardStatusUpdateOutput
  }
  return response.blockCard
}

export const useBlockCard = createMutationHook<CardStatusUpdateInput, CardStatusUpdateOutput>(blockCard)

/**
 * Unblock Card
 * @param CardStatusUpdateInput - input object containing cardId
 * @param context - reference to the AbortController used to abort the call (optional)
 * @returns Promise<CardStatusUpdateOutput>
 */
export const unblockCard = async (
  cardStatusUpdateInput: CardStatusUpdateInput,
  context?: DefaultContext,
): Promise<CardStatusUpdateOutput> => {
  const response = (await mutationPromise(
    {
      name: 'unblockCard',
      query: `
      mutation unblockCard($cardStatusUpdateInput: CardStatusUpdateInput!) {
        unblockCard(cardStatusUpdateInput: $cardStatusUpdateInput) {
          ... on CardStatusUpdateSuccess {
            __typename
            status
          }
          ... on CardStatusUpdateError {
            __typename
            errorType
          }
        }
      }
    `,
    },
    { cardStatusUpdateInput },
    context,
  )) as {
    unblockCard: CardStatusUpdateOutput
  }
  return response.unblockCard
}

export const useUnblockCard = createMutationHook<CardStatusUpdateInput, CardStatusUpdateOutput>(unblockCard)

/**
 * Report Lost ot Stolen Card
 * @param CardStatusUpdateInput - input object containing cardId
 * @param context - reference to the AbortController used to abort the call (optional)
 * @returns Promise<CardStatusUpdateOutput>
 */
export const reportLostOrStolenCard = async (
  cardStatusUpdateInput: CardStatusUpdateInput,
  context?: DefaultContext,
): Promise<CardStatusUpdateOutput> => {
  const response = (await mutationPromise(
    {
      name: 'reportLostStolen',
      query: `
          mutation reportLostStolen($cardStatusUpdateInput: CardStatusUpdateInput!) {
          reportLostOrStolenCard(cardStatusUpdateInput: $cardStatusUpdateInput) {
            ... on CardStatusUpdateSuccess {
              __typename
              status
            }
            ... on CardStatusUpdateError {
              __typename
              errorType
            }
          }
      }
    `,
    },
    { cardStatusUpdateInput },
    context,
  )) as {
    reportLostStolen: CardStatusUpdateOutput
  }
  return response.reportLostStolen
}

export const useReportLostOrStolenCard = createMutationHook<CardStatusUpdateInput, CardStatusUpdateOutput>(
  reportLostOrStolenCard,
)

/**
 * Activate Card
 * @param CardStatusUpdateInput - input object containing cardId
 * @param context - reference to the AbortController used to abort the call (optional)
 * @returns Promise<CardStatusUpdateOutput>
 */
export const activateCard = async (
  activateCardInput: ActivateCardInput,
  context?: DefaultContext,
): Promise<CardStatusUpdateOutput> => {
  const response = (await mutationPromise(
    {
      name: 'unblockCard',
      query: `
      mutation activateCard($activateCardInput: ActivateCardInput!) {
        activateCard(activateCardInput: $activateCardInput) {
          ... on CardStatusUpdateSuccess {
            __typename
            status
          }
          ... on CardStatusUpdateError {
            __typename
            errorType
          }
        }
      }
    `,
    },
    { activateCardInput },
    context,
  )) as {
    activateCard: CardStatusUpdateOutput
  }
  return response.activateCard
}

export const useActivateCard = createMutationHook<ActivateCardInput, CardStatusUpdateOutput>(activateCard)
// External Accounts

export const editLinkedAchAccountName = async (
  editLinkedAchAccountNameInput: EditLinkedAchAccountNameInput,
  context?: DefaultContext,
): Promise<EditLinkedAchAccountNameOutput> => {
  const response = (await mutationPromise(
    {
      name: 'editLinkedAchAccountName',
      query: `
            mutation editLinkedAchAccountName($editLinkedAchAccountNameInput: EditLinkedAchAccountNameInput!) {
              editLinkedAchAccountName(editLinkedAchAccountNameInput: $editLinkedAchAccountNameInput) {
                ... on LinkedAchAccount {
                  __typename
                  alias
                  name
                  mask
                  achType
                  linkedAccountStatus
                }
                ... on PatchAchAccountError {
                  __typename
                  errorType
                }
              }
            }
      `,
    },
    { editLinkedAchAccountNameInput },
    context,
  )) as { editLinkedAchAccountName: EditLinkedAchAccountNameOutput }
  return response.editLinkedAchAccountName
}

export const useEditLinkedAchAccountName = createMutationHook<
  EditLinkedAchAccountNameInput,
  EditLinkedAchAccountNameOutput
>(editLinkedAchAccountName)

export const linkAchAccount = async (
  linkAchAccountInput: LinkAchAccountInput,
  context?: DefaultContext,
): Promise<LinkAchAccountOutput> => {
  const response = (await mutationPromise(
    {
      name: 'linkAchAccount',
      query: `
        mutation linkAchAccount($linkAchAccountInput: LinkAchAccountInput!) {
          linkAchAccount(linkAchAccountInput: $linkAchAccountInput) {
            ... on LinkAchAccountSuccess {
              __typename
              alias
              displayName
              mask
              linkedAccountStatus
            }
            ... on LinkAchAccountError {
              __typename
              errorType
            }
          }
        }
      `,
    },
    { linkAchAccountInput },
    context,
  )) as { linkAchAccount: LinkAchAccountOutput }
  return response.linkAchAccount
}

export const useLinkAchAccount = createMutationHook<LinkAchAccountInput, LinkAchAccountOutput>(linkAchAccount)

export const unlinkAchAccount = async (
  unlinkAchAccountInput: UnlinkAchAccountInput,
  context?: DefaultContext,
): Promise<UnlinkAchAccountOutput> => {
  const response = (await mutationPromise(
    {
      name: 'unlinkAchAccount',
      query: `
            mutation unlinkAchAccount($unlinkAchAccountInput: UnlinkAchAccountInput!) {
              unlinkAchAccount(unlinkAchAccountInput: $unlinkAchAccountInput) {
                isSuccessful
              }
            }
      `,
    },
    { unlinkAchAccountInput },
    context,
  )) as { unlinkAchAccount: UnlinkAchAccountOutput }
  return response.unlinkAchAccount
}

export const useUnlinkAchAccount = createMutationHook<UnlinkAchAccountInput, UnlinkAchAccountOutput>(unlinkAchAccount)

export const createPlaidLinkTokenV2 = async (
  createPlaidLinkTokenV2Input: CreatePlaidLinkTokenV2Input,
  context?: DefaultContext,
): Promise<CreatePlaidLinkTokenV2Output> => {
  const response = (await gqlPromise(
    {
      name: 'createPlaidLinkTokenV2',
      query: `
            query createPlaidLinkTokenV2($createPlaidLinkTokenV2Input: CreatePlaidLinkTokenV2Input!) {
              createPlaidLinkTokenV2(createPlaidLinkTokenV2Input: $createPlaidLinkTokenV2Input) {
                ... on PlaidLinkToken {
                  __typename
                  linkToken
                  expiration
                }
                ... on CreatePlaidLinkTokenV2Error {
                  __typename
                  errorType
                }
              }
            }
      `,
    },
    { createPlaidLinkTokenV2Input },
    context,
  )) as { createPlaidLinkTokenV2: CreatePlaidLinkTokenV2Output }
  return response.createPlaidLinkTokenV2
}

export const linkPlaidAchAccount = async (
  linkPlaidAchAccountInput: LinkPlaidAchAccountInput,
  context?: DefaultContext,
): Promise<LinkPlaidAchAccountOutput> => {
  const response = (await mutationPromise(
    {
      name: 'linkPlaidAchAccount',
      query: `
        mutation linkPlaidAchAccount($linkPlaidAchAccountInput: LinkPlaidAchAccountInput!) {
          linkPlaidAchAccount(linkPlaidAchAccountInput: $linkPlaidAchAccountInput) {
            ... on LinkedAchAccount {
              __typename
              alias
              name
              mask
              achType
              linkedAccountStatus
            }
            ... on LinkPlaidAchAccountError {
              __typename
              errorType
            }
          }
        }
      `,
    },
    { linkPlaidAchAccountInput },
    context,
  )) as { linkPlaidAchAccount: LinkPlaidAchAccountOutput }
  return response.linkPlaidAchAccount
}

export const useLinkPlaidAchAccount = createMutationHook<LinkPlaidAchAccountInput, LinkPlaidAchAccountOutput>(
  linkPlaidAchAccount,
)

export const createPlaidMultiLinkToken = async (
  createPlaidMultiLinkTokenInput: CreatePlaidMultiLinkTokenInput,
  context?: DefaultContext,
): Promise<CreatePlaidLinkTokenV2Output> => {
  const response = (await gqlPromise(
    {
      name: 'createPlaidMultiLinkToken',
      query: `
        mutation createPlaidMultiLinkToken($createPlaidMultiLinkTokenInput: CreatePlaidMultiLinkTokenInput!) {
          createPlaidMultiLinkToken(createPlaidMultiLinkTokenInput: $createPlaidMultiLinkTokenInput) {
            ... on PlaidLinkToken {
              __typename
              linkToken
              expiration
            }
            ... on CreatePlaidLinkTokenV2Error  {
              __typename
              errorType
            }
          }
        }
      `,
    },
    {
      createPlaidMultiLinkTokenInput,
    },
    context,
  )) as { createPlaidMultiLinkToken: CreatePlaidLinkTokenV2Output }
  return response.createPlaidMultiLinkToken
}

export const useCreatePlaidMultiLinkToken = createMutationHook<
  CreatePlaidMultiLinkTokenInput,
  CreatePlaidLinkTokenV2Output
>(createPlaidMultiLinkToken)

export const unlinkExternalAccount = async (
  unlinkExternalAccountInput: UnlinkExternalAccountInput,
  context?: DefaultContext,
): Promise<UnlinkExternalAccountResult> => {
  const response = (await mutationPromise(
    {
      name: 'unlinkExternalAccount',
      query: `
        mutation unlinkExternalAccount($unlinkExternalAccountInput: UnlinkExternalAccountInput!) {
          unlinkExternalAccount(unlinkExternalAccountInput: $unlinkExternalAccountInput) {
            ... on UnlinkExternalAccountOutput {
              __typename
              isSuccessful
            }
            ... on UnlinkExternalAccountError {
              __typename
              errorType
            }
          }
        }
      `,
    },
    { unlinkExternalAccountInput },
    context,
  )) as { unlinkExternalAccount: UnlinkExternalAccountResult }
  return response.unlinkExternalAccount
}

export const useUnlinkExternalAccount = createMutationHook<UnlinkExternalAccountInput, UnlinkExternalAccountResult>(
  unlinkExternalAccount,
)

export const initiateRelinkExternalInstitution = async (
  initiateRelinkExternalAccountInput: InitiateRelinkExternalAccountInput,
  context?: DefaultContext,
): Promise<InitiateRelinkExternalAccountOutput> => {
  const response = (await mutationPromise(
    {
      name: 'initiateRelinkExternalAccount',
      query: `
        mutation initiateRelinkExternalAccount($initiateRelinkExternalAccountInput: InitiateRelinkExternalAccountInput!) {
          initiateRelinkExternalAccount(initiateRelinkExternalAccountInput: $initiateRelinkExternalAccountInput) {
            ... on PlaidLinkToken {
              __typename
              linkToken
              expiration
            }
            ... on RelinkExternalAccountError  {
              __typename
              errorType
            }

          }
        }
      `,
    },
    { initiateRelinkExternalAccountInput },
    context,
  )) as { initiateRelinkExternalAccount: InitiateRelinkExternalAccountOutput }
  return response.initiateRelinkExternalAccount
}

export const useInitiateRelinkExternalInstitution = createMutationHook<
  InitiateRelinkExternalAccountInput,
  InitiateRelinkExternalAccountOutput
>(initiateRelinkExternalInstitution)

export const completeRelinkExternalInstitution = async (
  completeRelinkExternalAccountInput: CompleteRelinkExternalAccountInput,
  context?: DefaultContext,
): Promise<CompleteRelinkExternalAccountResult> => {
  const response = (await mutationPromise(
    {
      name: 'completeRelinkExternalAccount',
      query: `
        mutation completeRelinkExternalAccount($completeRelinkExternalAccountInput: CompleteRelinkExternalAccountInput!) {
          completeRelinkExternalAccount(completeRelinkExternalAccountInput: $completeRelinkExternalAccountInput) {
            ... on CompleteRelinkExternalAccountOutput {
              __typename
              isSuccessful
            }
            ... on RelinkExternalAccountError {
              __typename
              errorType
            }
          }
        }
      `,
    },
    { completeRelinkExternalAccountInput },
    context,
  )) as { completeRelinkExternalAccount: CompleteRelinkExternalAccountResult }
  return response.completeRelinkExternalAccount
}

export const useCompleteRelinkExternalAccount = createMutationHook<
  CompleteRelinkExternalAccountInput,
  CompleteRelinkExternalAccountResult
>(completeRelinkExternalInstitution)

export const unlinkExternalAccountsByInstitution = async (
  unlinkExternalAccountsByInstitutionInput: UnlinkExternalAccountsByInstitutionInput,
  context?: DefaultContext,
): Promise<UnlinkExternalAccountsByInstitutionResult> => {
  const response = (await mutationPromise(
    {
      name: 'unlinkExternalAccountsByInstitution',
      query: `
        mutation unlinkExternalAccountsByInstitution($unlinkExternalAccountsByInstitutionInput: UnlinkExternalAccountsByInstitutionInput!) {
          unlinkExternalAccountsByInstitution(unlinkExternalAccountsByInstitutionInput: $unlinkExternalAccountsByInstitutionInput) {
            ... on UnlinkExternalAccountsByInstitutionOutput {
              __typename
              isSuccessful
            }
            ... on UnlinkExternalAccountsByInstitutionError {
              __typename
              errorType
            }
          }
        }
      `,
    },
    { unlinkExternalAccountsByInstitutionInput },
    context,
  )) as { unlinkExternalAccountsByInstitution: UnlinkExternalAccountsByInstitutionResult }
  return response.unlinkExternalAccountsByInstitution
}

export const useUnlinkExternalAccountsByInstitution = createMutationHook<
  UnlinkExternalAccountsByInstitutionInput,
  UnlinkExternalAccountsByInstitutionResult
>(unlinkExternalAccountsByInstitution)

export const initiateUpdatePlaidExternalAccount = async (
  initiateUpdatePlaidExternalAccountInput: InitiateUpdatePlaidExternalAccountInput,
  context?: DefaultContext,
): Promise<InitiateUpdatePlaidExternalAccountResult> => {
  const response = (await mutationPromise(
    {
      name: 'initiateUpdatePlaidExternalAccount',
      query: `
        mutation initiateUpdatePlaidExternalAccount($initiateUpdatePlaidExternalAccountInput: InitiateUpdatePlaidExternalAccountInput!) {
          initiateUpdatePlaidExternalAccount(initiateUpdatePlaidExternalAccountInput: $initiateUpdatePlaidExternalAccountInput) {
            ... on PlaidLinkToken {
              __typename
              linkToken
              expiration
            }
            ... on UpdatePlaidExternalAccountError {
              __typename
              errorType
            }
          }
        }
      `,
    },
    { initiateUpdatePlaidExternalAccountInput },
    context,
  )) as { initiateUpdatePlaidExternalAccount: InitiateUpdatePlaidExternalAccountResult }
  return response.initiateUpdatePlaidExternalAccount
}

export const useInitiateUpdatePlaidExternalAccount = createMutationHook<
  InitiateUpdatePlaidExternalAccountInput,
  InitiateUpdatePlaidExternalAccountResult
>(initiateUpdatePlaidExternalAccount)

export const completeUpdatePlaidExternalAccount = async (
  completeUpdatePlaidExternalAccountInput: CompleteUpdatePlaidExternalAccountInput,
  context?: DefaultContext,
): Promise<CompleteUpdatePlaidExternalAccountResult> => {
  const response = (await mutationPromise(
    {
      name: 'completeUpdatePlaidExternalAccount',
      query: `
        mutation completeUpdatePlaidExternalAccount($completeUpdatePlaidExternalAccountInput: CompleteUpdatePlaidExternalAccountInput!) {
          completeUpdatePlaidExternalAccount(completeUpdatePlaidExternalAccountInput: $completeUpdatePlaidExternalAccountInput) {
            ... on CompleteUpdatePlaidExternalAccountSuccess {
              __typename
              isSuccessful
            }
            ... on UpdatePlaidExternalAccountError {
              __typename
              errorType
            }
          }
        }
      `,
    },
    { completeUpdatePlaidExternalAccountInput },
    context,
  )) as { completeUpdatePlaidExternalAccount: CompleteUpdatePlaidExternalAccountResult }
  return response.completeUpdatePlaidExternalAccount
}

export const useCompleteUpdatePlaidExternalAccount = createMutationHook<
  CompleteUpdatePlaidExternalAccountInput,
  CompleteUpdatePlaidExternalAccountResult
>(completeUpdatePlaidExternalAccount)
